import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Sparkles, Zap, Cpu, MessageSquare, Target } from 'lucide-react';
import LiveCounter from '../components/landing/LiveCounter';
import InteractiveDemo from '../components/landing/InteractiveDemo';
import AgentRoster from '../components/landing/AgentRoster';
import GrowthMap from '../components/landing/GrowthMap';
import ViralHallOfFame from '../components/landing/ViralHallOfFame';
import ThemeToggle from '../components/ThemeToggle';

/* ═══════════════════════════════════════════════════════════════
   REELSCRIPT · ATELIER landing — restored original copy, new style
   - Dual-theme via .dark class + --atelier-* tokens
   - Electric Coral as sole accent (replaces neon lime)
   - Editorial serif headline + 1px hairlines + paper grain
   - All legacy sub-components auto-map via index.css Atelier scope
   ═══════════════════════════════════════════════════════════════ */

const ease = [0.25, 1, 0.5, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease } },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const Section = ({ children, className = '', id }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      id={id}
      ref={ref}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={stagger}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ═══ Hero coral/mint nebulas (Atelier paper ambience) ═══ */
const HeroAmbience = () => (
  <>
    <div
      className="absolute pointer-events-none"
      style={{
        top: '12%',
        right: '-8%',
        width: '58%',
        height: '58%',
        background:
          'radial-gradient(circle at center, var(--atelier-coral) 0%, transparent 65%)',
        opacity: 0.07,
        filter: 'blur(80px)',
      }}
    />
    <div
      className="absolute pointer-events-none"
      style={{
        bottom: '8%',
        left: '-5%',
        width: '42%',
        height: '42%',
        background:
          'radial-gradient(circle at center, var(--atelier-mint) 0%, transparent 65%)',
        opacity: 0.08,
        filter: 'blur(70px)',
      }}
    />
  </>
);

/* ═══ Magnetic pointer-following button ═══ */
const MagneticButton = ({ children, onClick, className, ...props }) => {
  const ref = useRef(null);
  const handleMove = useCallback((e) => {
    if (window.innerWidth < 768) return;
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left - rect.width / 2) * 0.1;
    const y = (e.clientY - rect.top - rect.height / 2) * 0.1;
    ref.current.style.transform = `translate(${x}px, ${y}px)`;
  }, []);
  const handleLeave = () => {
    if (ref.current) ref.current.style.transform = '';
  };
  return (
    <button
      ref={ref}
      onClick={onClick}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={`transition-transform duration-200 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

/* ═══ Top nav ═══ */
const TopNav = ({ navigate }) => (
  <motion.header
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, ease }}
    className="fixed top-0 left-0 right-0 z-40 atelier-glass-nav"
  >
    <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="flex items-center gap-2.5 group"
        data-testid="landing-logo"
      >
        <div className="relative w-8 h-8 flex items-center justify-center">
          <div className="absolute inset-0 rounded-[10px] border border-atelier-lineHi" />
          <div
            className="w-2.5 h-2.5 rounded-sm bg-atelier-coral group-hover:rotate-45 transition-transform duration-500"
            style={{ transformOrigin: 'center' }}
          />
        </div>
        <div className="flex items-baseline gap-2">
          <span
            className="text-[17px] tracking-tight text-atelier-ink"
            style={{
              fontFamily: "'Noto Serif TC', 'Playfair Display', Georgia, serif",
              fontWeight: 500,
            }}
          >
            Reelscript
          </span>
          <span className="atelier-caption">AI</span>
        </div>
      </button>

      <nav className="hidden md:flex items-center gap-10">
        {[
          { h: '#demo', l: '試用' },
          { h: '#agents', l: '代理' },
          { h: '#growth', l: '路徑' },
          { h: '#hall-of-fame', l: '案例' },
          { h: '#cta', l: '開始' },
        ].map((it) => (
          <a
            key={it.h}
            href={it.h}
            className="atelier-caption hover:text-atelier-coral transition-colors duration-300"
          >
            {it.l}
          </a>
        ))}
      </nav>

      <div className="flex items-center gap-2">
        <ThemeToggle />
        <button
          onClick={() => navigate('/login')}
          className="hidden sm:inline-flex items-center h-9 px-3 text-sm text-atelier-mute hover:text-atelier-ink transition-colors"
          data-testid="landing-login-btn"
        >
          登入
        </button>
        <MagneticButton
          onClick={() => navigate('/login')}
          className="atelier-btn-primary h-9 text-sm"
          data-testid="landing-get-started-btn"
        >
          開始使用
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
        </MagneticButton>
      </div>
    </div>
  </motion.header>
);

/* ═══ Hero ═══ */
const Hero = ({ navigate }) => {
  const stats = [
    { n: '10M+', l: '累計協助流量' },
    { n: '90%', l: '節省創作時間' },
    { n: '24/7', l: 'AI 助理待命' },
  ];

  return (
    <section className="relative min-h-[92vh] flex items-center justify-center pt-24 atelier-paper overflow-hidden">
      <HeroAmbience />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={stagger}
        className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 w-full"
      >
        {/* Kicker */}
        <motion.div
          variants={fadeUp}
          className="flex items-center justify-center gap-3 mb-10"
        >
          <span className="h-px w-10 bg-atelier-lineHi" />
          <span className="atelier-caption">SYSTEM · ONLINE</span>
          <span className="w-1.5 h-1.5 rounded-full bg-atelier-coral atelier-breathe" />
        </motion.div>

        {/* Headline — original copy */}
        <motion.h1
          variants={fadeUp}
          className="text-center tracking-[-0.03em] leading-[0.98] mb-10 text-atelier-ink"
          style={{
            fontFamily: "'Noto Serif TC', 'Playfair Display', Georgia, serif",
            fontSize: 'clamp(44px, 9vw, 120px)',
            fontWeight: 500,
          }}
        >
          <span className="block">讓 AI 幫您打造</span>
          <span className="block relative">
            <em
              className="not-italic"
              style={{ color: 'var(--atelier-coral)', fontStyle: 'italic' }}
            >
              爆款
            </em>
            內容
            <span
              aria-hidden
              className="absolute -top-2 -right-3 md:-right-8 w-2.5 h-2.5 md:w-3 md:h-3 rounded-sm bg-atelier-coral"
              style={{ boxShadow: '0 0 28px rgba(255,84,54,0.5)' }}
            />
          </span>
        </motion.h1>

        {/* Sub */}
        <motion.p
          variants={fadeUp}
          className="text-center text-atelier-mute max-w-xl mx-auto mb-12 text-[15px] md:text-base"
          style={{ lineHeight: 1.85 }}
        >
          輸入個人 IP 定位，AI 自動使用爆款邏輯生成 Reels 文案和腳本。
          <br className="hidden md:block" />
          節省{' '}
          <span className="text-atelier-coral font-medium">90%</span>{' '}
          創作時間，<span className="text-atelier-ink">專為華語內容創作者</span>
          設計。
        </motion.p>

        {/* CTA row */}
        <motion.div
          variants={fadeUp}
          className="flex items-center justify-center gap-3 mb-16 flex-wrap"
        >
          <MagneticButton
            onClick={() => navigate('/login')}
            className="atelier-btn-primary atelier-glow-coral h-12 px-8 text-[15px]"
            data-testid="get-started-btn"
          >
            立即開始使用
            <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
          </MagneticButton>
          <button
            onClick={() =>
              document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' })
            }
            className="atelier-btn-ghost h-12 px-8 text-[15px]"
            data-testid="learn-more-btn"
          >
            立即試用 Demo
          </button>
        </motion.div>

        {/* Stats strip — 1px hairlines Atelier style */}
        <motion.div
          variants={fadeUp}
          className="grid grid-cols-3 max-w-3xl mx-auto border-y border-atelier-line"
        >
          {stats.map((s, i) => (
            <div
              key={i}
              className={`text-center px-4 py-6 ${
                i > 0 ? 'border-l border-atelier-line' : ''
              }`}
            >
              <div
                className="text-atelier-ink text-2xl md:text-3xl tabular-nums tracking-tight"
                style={{
                  fontFamily:
                    "'Noto Serif TC', 'Playfair Display', Georgia, serif",
                  fontWeight: 500,
                }}
              >
                {s.n}
              </div>
              <div className="atelier-caption mt-1.5">{s.l}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Bottom fade into next section */}
      <div
        className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
        style={{
          background:
            'linear-gradient(to top, var(--atelier-bg), transparent)',
        }}
      />
    </section>
  );
};

/* ═══ Tech benefits strip — original 4 benefits ═══ */
const Benefits = () => (
  <section id="benefits" className="relative py-20 md:py-28 border-y border-atelier-line">
    <div
      className="absolute inset-0 pointer-events-none opacity-40"
      style={{
        background:
          'radial-gradient(ellipse 50% 40% at 20% 50%, rgba(255,84,54,0.05) 0%, transparent 70%), radial-gradient(ellipse 50% 40% at 80% 50%, rgba(111,181,138,0.05) 0%, transparent 70%)',
      }}
    />

    <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
      <Section className="grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-6">
          <motion.div variants={fadeUp} className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-atelier-lineHi" />
            <span className="atelier-caption">STACK · AI POWERED</span>
          </motion.div>
          <motion.h2
            variants={fadeUp}
            className="text-atelier-ink tracking-[-0.02em] leading-[1.1] text-3xl md:text-4xl lg:text-5xl mb-6"
            style={{
              fontFamily: "'Noto Serif TC', 'Playfair Display', Georgia, serif",
              fontWeight: 500,
            }}
          >
            為什麼全球創作者
            <br />
            都選擇{' '}
            <em
              className="not-italic"
              style={{ color: 'var(--atelier-coral)', fontStyle: 'italic' }}
            >
              Reelscript
            </em>
            ？
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="text-atelier-mute max-w-lg text-base"
            style={{ lineHeight: 1.85 }}
          >
            我們結合最先進的大語言模型、爆款影片資料庫與專為華語受眾設計的創作邏輯 —
            用一套系統解決腳本、選題、Hook、結構四大難題。
          </motion.p>
        </div>

        <div className="md:col-span-6 grid grid-cols-2 gap-4">
          {[
            { icon: <Zap className="w-4 h-4" strokeWidth={1.6} />, k: '節省 90%', v: '內容創作時間' },
            { icon: <Cpu className="w-4 h-4" strokeWidth={1.6} />, k: '爆款邏輯', v: '3000+ 成功案例學習' },
            { icon: <MessageSquare className="w-4 h-4" strokeWidth={1.6} />, k: '多代理', v: '專業子代理群組協作' },
            { icon: <Target className="w-4 h-4" strokeWidth={1.6} />, k: 'IP 定位', v: 'AI 推薦差異化策略' },
          ].map((b, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              className="atelier-card p-5 group hover:border-atelier-coral/50 transition-colors"
            >
              <div className="w-8 h-8 rounded-lg bg-atelier-surface2 border border-atelier-line flex items-center justify-center text-atelier-mute group-hover:text-atelier-coral group-hover:border-atelier-coral/40 mb-4 transition-colors">
                {b.icon}
              </div>
              <div
                className="text-atelier-ink text-xl mb-1 tracking-tight"
                style={{
                  fontFamily:
                    "'Noto Serif TC', 'Playfair Display', Georgia, serif",
                  fontWeight: 500,
                }}
              >
                {b.k}
              </div>
              <div className="text-xs text-atelier-mute">{b.v}</div>
            </motion.div>
          ))}
        </div>
      </Section>
    </div>
  </section>
);

/* ═══ Big CTA — original copy ═══ */
const FinalCTA = ({ navigate }) => (
  <section id="cta" className="relative py-28 md:py-36 overflow-hidden atelier-paper">
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          'radial-gradient(ellipse 70% 60% at 50% 70%, var(--atelier-coral) 0%, transparent 70%)',
        opacity: 0.08,
      }}
    />

    <Section className="max-w-4xl mx-auto px-6 md:px-12 text-center relative">
      <motion.div
        variants={fadeUp}
        className="flex items-center justify-center gap-3 mb-8"
      >
        <span className="h-px w-8 bg-atelier-lineHi" />
        <span className="atelier-caption">BEGIN · NOW</span>
        <span className="h-px w-8 bg-atelier-lineHi" />
      </motion.div>

      <motion.h2
        variants={fadeUp}
        className="tracking-[-0.02em] leading-[1.05] text-atelier-ink text-4xl md:text-6xl mb-6"
        style={{
          fontFamily: "'Noto Serif TC', 'Playfair Display', Georgia, serif",
          fontWeight: 500,
        }}
      >
        準備好打造
        <br />
        你的{' '}
        <em
          className="not-italic"
          style={{ color: 'var(--atelier-coral)', fontStyle: 'italic' }}
        >
          個人品牌
        </em>{' '}
        了嗎？
      </motion.h2>
      <motion.p
        variants={fadeUp}
        className="text-atelier-mute text-base md:text-lg mb-10 max-w-lg mx-auto"
        style={{ lineHeight: 1.85 }}
      >
        立即註冊，3 分鐘內開始創作屬於你的第一個爆款 Reels。
      </motion.p>
      <motion.div variants={fadeUp}>
        <MagneticButton
          onClick={() => navigate('/login')}
          className="atelier-btn-primary atelier-glow-coral h-14 px-10 text-[16px]"
          data-testid="cta-register-btn"
        >
          立即免費註冊
          <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
        </MagneticButton>
      </motion.div>
    </Section>
  </section>
);

/* ═══ Footer ═══ */
const Footer = () => (
  <footer className="border-t border-atelier-line py-10">
    <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-2.5">
        <div className="relative w-7 h-7 flex items-center justify-center">
          <div className="absolute inset-0 rounded-md border border-atelier-lineHi" />
          <div className="w-2 h-2 rounded-sm bg-atelier-coral" />
        </div>
        <span
          className="text-atelier-ink text-sm tracking-tight"
          style={{
            fontFamily: "'Noto Serif TC', 'Playfair Display', Georgia, serif",
            fontWeight: 500,
          }}
        >
          Reelscript AI
        </span>
      </div>
      <div className="atelier-caption flex items-center gap-2">
        <Sparkles className="w-3 h-3 text-atelier-coral" strokeWidth={1.5} />
        © 2026 · ALL RIGHTS RESERVED
      </div>
    </div>
  </footer>
);

/* ═══ Landing ═══ */
const Landing = () => {
  const navigate = useNavigate();

  return (
    <div
      data-atelier="true"
      className="min-h-screen relative overflow-hidden"
      data-testid="landing-page"
    >
      {/* Top edge coral hairline */}
      <div
        className="fixed top-0 left-0 right-0 h-[2px] z-50 opacity-80"
        style={{
          background:
            'linear-gradient(to right, transparent, var(--atelier-coral), transparent)',
        }}
      />

      <TopNav navigate={navigate} />

      <main>
        <Hero navigate={navigate} />

        {/* Live Counter (original B) */}
        <LiveCounter />

        {/* Interactive Demo (original A) */}
        <InteractiveDemo />

        {/* AI Agent Roster (original F) */}
        <AgentRoster />

        {/* Growth Map (original D) */}
        <GrowthMap />

        {/* Viral Hall of Fame (original C) */}
        <ViralHallOfFame />

        {/* Tech benefits (original) */}
        <Benefits />

        {/* Big CTA (original) */}
        <FinalCTA navigate={navigate} />
      </main>

      <Footer />
    </div>
  );
};

export default Landing;
